import { Dashboard } from '@components/dashboard';
import { DashboardItem } from '@components/dashboard/item';
import { JobAlert } from '@components/job/workProgress/alert';
import { getCommitment, getJobStatus, sortByProp } from '@components/job/workProgress/helpers';
import { JobStageMenu } from '@components/job/workProgress/stage/menu';
import { Rescheduler } from '@components/rescheduler';
import { useJobContext } from '@contexts/job';
import { initialState } from '@contexts/job/constants';
import { JobStage as JobStageType } from '@root/globalTypes';
import { JobStageRemove } from '@components/job/workProgress/stage/remove';
import { JobTaskGroup } from '@components/job/workProgress/taskGroup';
import { groupStageTasks } from '@components/job/workProgress/taskGroup/helpers';
import { AlertCircle, CircleDashed } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const WorkProgress = () => {
  const { stages, isLoading, rescheduler, setState } = useJobContext();
  const { t } = useTranslation('app', { keyPrefix: 'pages.job.days' });

  return (
    <>
      <Dashboard
        isLoading={isLoading.stages}
        className="pl-3 pt-2 min-[900px]:grid-cols-[minmax(180px,1fr)_auto_auto_auto] max-[899px]:px-2">
        {sortByProp<JobStageType>(stages, 'position', 'asc').map((stage) => {
          const groups = groupStageTasks(stage.tasks);
          const notScheduled = groups.filter((group) => group.needsSchedule).length;
          const scheduledTasks = groups.flatMap((group) => group.days.flatMap((day) => day.tasks));
          const drafts = scheduledTasks.filter((task) => getCommitment(task) === 'draft').length;
          const rejected = scheduledTasks.filter(
            (task) => getCommitment(task) === 'rejected',
          ).length;

          return (
            <DashboardItem
              key={`dashboard-item-${stage.id}`}
              id={stage.id}
              title={
                <div className="flex flex-wrap items-center gap-2.5 max-[899px]:gap-x-2 max-[899px]:gap-y-1">
                  <h2 className="text-lg font-bold text-neutral-800 max-[899px]:text-base">
                    {stage.name}
                  </h2>
                  <JobAlert status={getJobStatus(stage)} alerts={stage.alerts} />
                  {Boolean(notScheduled) && (
                    <span className="flex items-center gap-1 rounded-full bg-warning-50 px-2.5 py-0.5 text-xs font-semibold text-warning-700">
                      <CircleDashed className="h-3.5 w-3.5" />
                      {t('tasksNotScheduled', { count: notScheduled })}
                    </span>
                  )}
                  {Boolean(rejected) && (
                    <span className="flex items-center gap-1 rounded-full bg-danger-50 px-2.5 py-0.5 text-xs font-semibold text-danger-600">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {t('stageRejected', { count: rejected })}
                    </span>
                  )}
                  {Boolean(drafts) && (
                    <span className="flex items-center gap-1 rounded-full border border-dashed border-neutral-300 bg-neutral-50 px-2.5 py-0.5 text-xs font-semibold text-neutral-600">
                      <CircleDashed className="h-3.5 w-3.5" />
                      {t('stageDrafts', { count: drafts })}
                    </span>
                  )}
                </div>
              }
              actions={
                <>
                  {stage.manuallyAdded && <JobStageRemove stageId={stage.id} name={stage.name} />}
                  <JobStageMenu
                    jobId={stage.jobId}
                    stageId={stage.id}
                    multi={stage.multiplicableTasks}
                  />
                </>
              }
              body={
                <div className="col-start-1 col-end-5 grid gap-y-2 rounded-b-lg bg-white px-3 pb-3 [grid-template-columns:subgrid] max-[899px]:flex max-[899px]:flex-col max-[899px]:px-2 max-[899px]:pb-2">
                  <div className="col-start-3 col-end-4 pt-1 text-center text-[10px] font-semibold uppercase tracking-wider text-neutral-400 max-[899px]:-mb-1 max-[899px]:w-12 max-[899px]:self-end">
                    {t('done')}
                  </div>
                  <div className="col-start-4 col-end-5 px-2.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 max-[899px]:hidden">
                    {t('notes')}
                  </div>
                  {groups.map((group) => (
                    <JobTaskGroup key={group.key} data={group} stage={stage} />
                  ))}
                </div>
              }
            />
          );
        })}
      </Dashboard>
      <Rescheduler {...rescheduler} setOpen={() => setState(initialState)} phoneLayout />
    </>
  );
};
